import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  FiUsers,
  FiCalendar,
  FiCheckSquare,
  FiTrendingUp,
  FiAlertTriangle,
  FiClock,
  FiPlus,
  FiEye,
  FiRefreshCw,
  FiActivity,
  FiBarChart2,
  FiBriefcase,
  FiUserCheck,
  FiArrowUp,
  FiArrowDown,
  FiPhone,
  FiGift,
  FiMapPin
} from 'react-icons/fi';
import { FaBirthdayCake } from 'react-icons/fa';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { dashboardService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Dashboard.css';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const CATEGORIAS_FILTRO = [
  { valor: 'todos', label: 'Todos' },
  { valor: 'crianca', label: 'Crianças' },
  { valor: 'jovem', label: 'Jovens' },
  { valor: 'adulto', label: 'Adultos' },
];

// Anos disponíveis no filtro (ex.: 5 anos atrás até 2 à frente)
const ANO_MIN = new Date().getFullYear() - 5;
const ANO_MAX = new Date().getFullYear() + 2;
const ANOS_OPCOES = Array.from({ length: ANO_MAX - ANO_MIN + 1 }, (_, i) => ANO_MIN + i);

const Dashboard = () => {
  const { canReadModule, canWriteModule, user } = useAuth();
  const perfilCriancas = user?.permissao === 'reuniao_criancas';
  const hoje = new Date();
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [presencaPorMes, setPresencaPorMes] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [loadingAniversario, setLoadingAniversario] = useState(true);
  const [filtroAniversario, setFiltroAniversario] = useState('todos');
  const [mesSelecionado, setMesSelecionado] = useState(hoje.getMonth() + 1);
  const [anoSelecionado, setAnoSelecionado] = useState(hoje.getFullYear());

  const nomeMesSelecionado = MESES[mesSelecionado - 1];

  const temDashGeral = canReadModule('colaboradores') || canReadModule('reunioes') || canReadModule('presencas');

  useEffect(() => {
    if (temDashGeral) carregarDados();
    else setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (canReadModule('irmandade')) {
      carregarAniversariantes(filtroAniversario, mesSelecionado, anoSelecionado);
    }
  }, [filtroAniversario, mesSelecionado, anoSelecionado]); // eslint-disable-line

  const carregarAniversariantes = async (categoria, mes, ano) => {
    try {
      setLoadingAniversario(true);
      const res = await dashboardService.getAniversariantes(
        categoria === 'todos' ? null : categoria,
        mes,
        ano
      );
      setAniversariantes(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erro ao carregar aniversariantes:', error);
    } finally {
      setLoadingAniversario(false);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [resumoData, presencaData] = await Promise.all([
        dashboardService.getResumo(),
        dashboardService.getPresencaPorMes()
      ]);
      
      setResumo(resumoData.data);
      setPresencaPorMes(presencaData.data);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      carregarDados(),
      carregarAniversariantes(filtroAniversario === 'todos' ? null : filtroAniversario, mesSelecionado, anoSelecionado)
    ]);
    setRefreshing(false);
    toast.success('Dados atualizados com sucesso!');
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'primary', link }) => (
    <div className={`stat-card stat-card-${color} animate-fadeIn hover-lift`}>
      <div className="stat-header">
        <div className="stat-icon">
          <Icon />
        </div>
        <div className="stat-trend">
          {trend && (
            <div className={`trend ${trend}`}>
              {trend === 'up' ? <FiArrowUp /> : <FiArrowDown />}
              <span>{trendValue}%</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-title">{title}</div>
      </div>
      
      {link && (
        <Link to={link} className="stat-link">
          <FiEye />
          Ver detalhes
        </Link>
      )}
    </div>
  );

  const QuickActionCard = ({ title, description, icon: Icon, link, color = 'primary' }) => (
    <Link to={link} className={`quick-action-card quick-action-${color} hover-lift animate-slideInRight`}>
      <div className="quick-action-icon">
        <Icon />
      </div>
      <div className="quick-action-content">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="quick-action-arrow">
        <FiArrowUp style={{ transform: 'rotate(45deg)' }} />
      </div>
    </Link>
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            family: 'Inter, sans-serif',
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        },
        ticks: {
          font: {
            family: 'Inter, sans-serif',
            size: 11
          },
          color: '#64748b'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: 'Inter, sans-serif',
            size: 11
          },
          color: '#64748b'
        }
      }
    }
  };

  const barData = {
    labels: presencaPorMes.map(item => format(new Date(item.mes), 'MMM/yyyy', { locale: ptBR })),
    datasets: [
      {
        label: 'Presenças',
        data: presencaPorMes.map(item => item.total_presencas),
        backgroundColor: 'rgba(14, 165, 233, 0.8)',
        borderColor: 'rgba(14, 165, 233, 1)',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      }
    ]
  };

  const doughnutData = {
    labels: ['Presentes', 'Ausentes'],
    datasets: [
      {
        data: [
          resumo?.total_presencas || 0,
          (resumo?.total_colaboradores || 0) - (resumo?.total_presencas || 0)
        ],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(239, 68, 68, 1)'
        ],
        borderWidth: 2,
        cutout: '60%'
      }
    ]
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-header">
          <div className="header-skeleton">
            <div className="skeleton skeleton-title"></div>
            <div className="skeleton skeleton-text"></div>
          </div>
        </div>
        
        <div className="dashboard-stats">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton skeleton-card"></div>
          ))}
        </div>
        
        <div className="dashboard-content grid md:grid-cols-2 gap-6">
          <div className="skeleton skeleton-card" style={{ height: '300px' }}></div>
          <div className="skeleton skeleton-card" style={{ height: '300px' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">

      {/* ── Atalhos Crianças — visíveis apenas para perfil reunião_criancas ── */}
      {perfilCriancas && (
        <div className="dash-criancas-hero animate-fadeIn">
          <div className="dash-criancas-titulo">
            <FiGift className="dash-criancas-icone" />
            <div>
              <h2>Visitas de Aniversário</h2>
              <p>Acesso rápido às telas mais usadas</p>
            </div>
          </div>
          <div className="dash-criancas-grid">
            <QuickActionCard
              title="Aniversariantes"
              description="Crianças com aniversário no mês"
              icon={FiGift}
              link="/reuniao-criancas/aniversariantes"
              color="primary"
            />
            <QuickActionCard
              title="Visitas Agendadas"
              description="Acompanhar e editar visitas marcadas"
              icon={FiCheckSquare}
              link="/reuniao-criancas/visitas-agendadas"
              color="success"
            />
            <QuickActionCard
              title="Reunião de Crianças"
              description="Presenças e agenda de reuniões"
              icon={FiUsers}
              link="/reuniao-criancas"
              color="warning"
            />
            <QuickActionCard
              title="Irmandade"
              description="Lista de membros e crianças"
              icon={FiMapPin}
              link="/irmandade"
              color="secondary"
            />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="dashboard-header animate-fadeIn">
        <div className="header-content">
          <div className="header-info">
            <h1>
              <FiActivity className="page-icon" />
              Dashboard
            </h1>
            <p>Visão geral do sistema de gestão de reuniões</p>
            <div className="header-meta">
              <span className="last-update">
                <FiClock />
                Atualizado agora
              </span>
            </div>
          </div>
          
          <div className="header-actions">
            <button 
              className="btn btn-secondary btn-icon"
              onClick={handleRefresh}
              disabled={refreshing}
              title="Atualizar dados"
            >
              <FiRefreshCw className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {temDashGeral && (
        <div className="dashboard-stats">
          {canReadModule('colaboradores') && (
            <StatCard
              title="Total de Colaboradores"
              value={resumo?.total_colaboradores || 0}
              icon={FiUsers}
              trend="up"
              trendValue="12"
              color="primary"
              link="/colaboradores"
            />
          )}
          
          {canReadModule('reunioes') && (
            <StatCard
              title="Reuniões Realizadas"
              value={resumo?.total_reunioes || 0}
              icon={FiCalendar}
              trend="up"
              trendValue="8"
              color="success"
              link="/reunioes"
            />
          )}
          
          {canReadModule('presencas') && (
            <StatCard
              title="Total de Presenças"
              value={resumo?.total_presencas || 0}
              icon={FiCheckSquare}
              trend="down"
              trendValue="3"
              color="warning"
              link="/presencas"
            />
          )}
          
          {canReadModule('cargos') && (
            <StatCard
              title="Cargos Ativos"
              value={resumo?.total_cargos || 0}
              icon={FiBriefcase}
              color="secondary"
              link="/cargos"
            />
          )}
        </div>
      )}

      {/* Aniversariantes do Mês */}
      {canReadModule('irmandade') && <div className="aniversariantes-section animate-fadeIn">
        <div className="aniversariantes-header">
          <div className="aniversariantes-title">
            <FaBirthdayCake className="aniversariantes-icon" />
            <div>
              <h3>Aniversariantes{perfilCriancas ? ' (Crianças)' : ''} de {nomeMesSelecionado} {anoSelecionado}</h3>
              <p>
                {loadingAniversario
                  ? 'Carregando...'
                  : `${aniversariantes.length} aniversariante${aniversariantes.length !== 1 ? 's' : ''} neste mês`}
              </p>
            </div>
          </div>

          <div className="aniversariantes-filtros-wrap">
            <div className="aniversariantes-filtro-data">
              <label>Período:</label>
              <div className="aniv-selects-row">
                <select
                  value={mesSelecionado}
                  onChange={(e) => setMesSelecionado(Number(e.target.value))}
                  className="aniv-select aniv-select-mes"
                  aria-label="Mês"
                >
                  {MESES.map((nome, i) => (
                    <option key={i} value={i + 1}>{nome}</option>
                  ))}
                </select>
                <select
                  value={anoSelecionado}
                  onChange={(e) => setAnoSelecionado(Number(e.target.value))}
                  className="aniv-select aniv-select-ano"
                  aria-label="Ano"
                >
                  {ANOS_OPCOES.map(ano => (
                    <option key={ano} value={ano}>{ano}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="aniv-btn-hoje"
                  onClick={() => {
                    const d = new Date();
                    setMesSelecionado(d.getMonth() + 1);
                    setAnoSelecionado(d.getFullYear());
                  }}
                  title="Voltar para o mês atual"
                >
                  Este mês
                </button>
              </div>
            </div>
            {!perfilCriancas && (
              <div className="aniversariantes-filtros">
                {CATEGORIAS_FILTRO.map(cat => (
                  <button
                    key={cat.valor}
                    className={`aniv-filtro-btn aniv-filtro-${cat.valor} ${filtroAniversario === cat.valor ? 'ativo' : ''}`}
                    onClick={() => setFiltroAniversario(cat.valor)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {loadingAniversario ? (
          <div className="aniversariantes-grid">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="skeleton skeleton-card aniv-skeleton"></div>
            ))}
          </div>
        ) : aniversariantes.length === 0 ? (
          <div className="aniversariantes-empty">
            <FaBirthdayCake />
            <p>Nenhum aniversariante em {nomeMesSelecionado} {anoSelecionado}{filtroAniversario !== 'todos' ? ' na categoria selecionada' : ''}.</p>
          </div>
        ) : (
          <div className="aniversariantes-grid">
            {aniversariantes.map(a => (
              <div
                key={a.id}
                className={`aniversariante-card aniv-cat-${a.categoria} ${a.ja_fez ? 'ja-fez' : 'vai-fazer'}`}
              >
                <div className="aniv-dia-bloco">
                  <span className="aniv-dia-num">{a.dia}</span>
                  <span className="aniv-dia-mes">{nomeMesSelecionado.slice(0, 3)}</span>
                </div>

                <div className="aniv-info">
                  <div className="aniv-nome">{a.nome_completo}</div>
                  <div className="aniv-meta">
                    <span className={`aniv-badge aniv-badge-${a.categoria}`}>
                      {a.categoria === 'crianca' ? 'Criança' : a.categoria === 'jovem' ? 'Jovem' : 'Adulto'}
                    </span>
                    <span className="aniv-idade">{a.idade_completando} anos</span>
                  </div>
                  {a.congregacao && (
                    <div className="aniv-congregacao">{a.congregacao}</div>
                  )}
                  {a.telefone && (
                    <div className="aniv-telefone">
                      <FiPhone size={11} />
                      {a.telefone}
                    </div>
                  )}
                </div>

                <div className="aniv-cake-icon">
                  <FaBirthdayCake />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>}

      {/* Main Content */}
      <div className="dashboard-content">
        {/* Charts Section */}
        {temDashGeral && <div className="charts-section">
          <div className="chart-card animate-slideInRight">
            <div className="chart-header">
              <h3>
                <FiBarChart2 />
                Presenças por Mês
              </h3>
              <p>Evolução das presenças ao longo do tempo</p>
            </div>
            <div className="chart-container">
              <Bar data={barData} options={chartOptions} />
            </div>
          </div>

          <div className="chart-card animate-slideInRight">
            <div className="chart-header">
              <h3>
                <FiTrendingUp />
                Taxa de Presença
              </h3>
              <p>Distribuição de presenças vs ausências</p>
            </div>
            <div className="chart-container">
              <Doughnut data={doughnutData} options={chartOptions} />
            </div>
          </div>
        </div>}

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <div className="section-header">
            <h3>Ações Rápidas</h3>
            <p>Acesse rapidamente as principais funcionalidades</p>
          </div>
          
          <div className="quick-actions-grid">
            {canWriteModule('reunioes') && (
              <QuickActionCard
                title="Nova Reunião"
                description="Agendar uma nova reunião"
                icon={FiPlus}
                link="/reunioes/nova"
                color="primary"
              />
            )}
            
            {canWriteModule('colaboradores') && (
              <QuickActionCard
                title="Novo Colaborador"
                description="Cadastrar um colaborador"
                icon={FiUserCheck}
                link="/colaboradores/novo"
                color="success"
              />
            )}
            
            {canReadModule('presencas') && (
              <QuickActionCard
                title="Registrar Presença"
                description="Controlar presença em reunião"
                icon={FiCheckSquare}
                link="/presencas"
                color="warning"
              />
            )}
            
            {canReadModule('relatorios') && (
              <QuickActionCard
                title="Relatórios"
                description="Visualizar relatórios detalhados"
                icon={FiBarChart2}
                link="/relatorios"
                color="secondary"
              />
            )}

            {canReadModule('reuniao_criancas') && (
              <QuickActionCard
                title="Reunião de Crianças"
                description="Presença e visitas de aniversário"
                icon={FiUsers}
                link="/reuniao-criancas"
                color="primary"
              />
            )}

            {canReadModule('irmandade') && (
              <QuickActionCard
                title="Irmandade"
                description="Gerenciar cadastros"
                icon={FiUsers}
                link="/irmandade"
                color="success"
              />
            )}
          </div>
        </div>

        {/* Recent Activity */}
        {temDashGeral && <div className="recent-activity-section">
          <div className="section-header">
            <h3>
              <FiActivity />
              Atividade Recente
            </h3>
            <p>Últimas ações realizadas no sistema</p>
          </div>
          
          <div className="activity-feed">
            <div className="activity-item animate-fadeIn">
              <div className="activity-icon success">
                <FiCheckSquare />
              </div>
              <div className="activity-content">
                <div className="activity-title">Nova reunião criada</div>
                <div className="activity-description">Reunião Mensal - Janeiro 2024</div>
                <div className="activity-time">há 2 horas</div>
              </div>
            </div>
            
            <div className="activity-item animate-fadeIn">
              <div className="activity-icon primary">
                <FiUsers />
              </div>
              <div className="activity-content">
                <div className="activity-title">Colaborador cadastrado</div>
                <div className="activity-description">João Silva foi adicionado ao sistema</div>
                <div className="activity-time">há 4 horas</div>
              </div>
            </div>
            
            <div className="activity-item animate-fadeIn">
              <div className="activity-icon warning">
                <FiAlertTriangle />
              </div>
              <div className="activity-content">
                <div className="activity-title">Presença registrada</div>
                <div className="activity-description">15 presenças confirmadas na reunião</div>
                <div className="activity-time">há 1 dia</div>
              </div>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
};

export default Dashboard; 